/* Clients — CR-01…CR-05 + CR-11…CR-17 (Bible §10/§49/§54/§65 · audit §12).
   The intent on arrival is a question — qui revient ? — so four cohorts lead,
   each already a usable group; the directory is a tab, not the page.
   Workspace-scoped, consent-aware; the excluded count is part of the UI.

   Wired 07/10 onto GET /v1/app/clients (contract 10 §10.4): the API sends codes
   (stage, state, counts, ISO dates) and this screen writes the French. Every
   cohort figure in a hint or a definition reads `thresholds` from the answer
   (D-85: habitué = 3 visits in 180 days, perdu de vue = 90 days), never a
   number of its own. D-84: the groups exist so the merchant re-invites the
   right customers — each one says who, and how to get them back. */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Button, Card, EmptyState, Hint, Icon, Input, KV, ListRow, Note, PageHeader, Pill, Segmented, Select, Skeleton, Tabs, Tile, Tiles } from '@/ui'
import { ApiError, useCampaigns, useClients, useLocations, type ClientRow, type ClientStage, type ClientsCohorts } from '@/lib/api'
import { ALL, useScope } from '@/lib/scope'
import { allCampaigns, getCampaigns } from '@/lib/campaign'
import { dateFr, dateTimeFr, getLedger, giftDetailPath, GiftLedger, rowsOfClient, STATUS } from '@/routes/cadeaux/Ledger'

type Vue = 'liste' | 'profil' | 'segment' | 'droits' | 'cadeaux'
  | 'activation' | 'integrations' | 'connexion' | 'gtm' | 'journal' | 'segments' | 'historique'
const VUES: Vue[] = ['liste', 'profil', 'segment', 'droits', 'cadeaux',
  'activation', 'integrations', 'connexion', 'gtm', 'journal', 'segments', 'historique']

/* the codes, in the merchant's words */
const STAGE: Record<ClientStage, { label: string; tone: 'ok' | 'done' | 'warn' | 'spark' }> = {
  habitue: { label: 'Habitué·e', tone: 'spark' },
  actif: { label: 'Revenu·e', tone: 'ok' },
  nouveau: { label: 'Première visite', tone: 'done' },
  perdu_de_vue: { label: 'Perdu·e de vue', tone: 'warn' },
}
/* v3.3 WF-024 · « Revenus » joins the groups: two visits or more and still seen (D-85: not perdu de vue) — the same
   reading as the « Qui revient » tab, so one word everywhere; the app counts « visites » (Scope v5 §2). */
type Filtre = 'tous' | 'habitue' | 'revenu' | 'cadeau' | 'perdu'
const FILTRES: Record<Filtre, (r: ClientRow) => boolean> = {
  tous: () => true,
  habitue: r => r.stage === 'habitue',
  revenu: r => r.visits > 1 && r.stage !== 'perdu_de_vue',
  cadeau: r => r.giftsHeld > 0,
  perdu: r => r.stage === 'perdu_de_vue',
}
/** 180 → « 6 mois », 90 → « 90 jours »: whole months read as months from 60 days up */
const duree = (j: number) => (j >= 60 && j % 30 === 0 ? `${j / 30} mois` : `${j} jours`)
const nom = (r: ClientRow) => r.firstName ?? 'Sans prénom'
const jour = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) })
}
const cadeaux = (r: ClientRow) => [
  r.giftsHeld ? `${r.giftsHeld} à utiliser` : null,
  r.giftsExpired ? `${r.giftsExpired} expiré${r.giftsExpired > 1 ? 's' : ''}` : null,
].filter(Boolean).join(' · ') || '—'
const visites = (n: number) => `${n} visite${n > 1 ? 's' : ''}`
const NOT_MEASURED = 'pas encore mesuré'
/** « Nom, mot-clé… » against the first name and the address */
const matches = (r: ClientRow, q: string) => { const s = q.trim().toLowerCase(); return !s || [r.firstName, r.email].some(x => (x ?? '').toLowerCase().includes(s)) }

export default function Clients() {
  const [scope] = useScope()
  const { data, isPending, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useClients(scope !== ALL ? scope : undefined)
  const [params, setParams] = useSearchParams()
  const forced = params.get('vue') as Vue | null
  const [vue, setVue] = useState<Vue>(forced && VUES.includes(forced) ? forced : 'liste')
  const [tab, setTab] = useState<'reviennent' | 'tous' | 'groupes'>(() => { const o = params.get('onglet'); return o === 'tous' || o === 'groupes' ? o : 'reviennent' })
  const [filtre, setFiltre] = useState<Filtre>('tous')
  const [q, setQ] = useState('')
  const [choisi, setChoisi] = useState<ClientRow | null>(null)
  const nav = useNavigate()
  const { data: locations } = useLocations()
  const { data: apiCampaigns } = useCampaigns()
  const ctxName = scope !== ALL ? (locations ?? []).find(l => l.id === scope)?.name ?? null : null

  if (isPending) return <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  /* staff: the API answers 403 forbidden_role with its own French sentence */
  if (isError) return (
    <div className="page" data-testid="clients-erreur">
      <PageHeader title="Clients" />
      <Note tone={error instanceof ApiError && error.status === 403 ? 'candour' : 'crit'}>
        {error instanceof ApiError && error.status === 403 ? error.message : 'La liste des clients n’a pas pu être chargée. Réessayez dans un instant.'}
      </Note>
    </div>
  )
  const first: ClientsCohorts = data.pages[0]
  const d = first.cohorts
  const th = first.thresholds
  const items = data.pages.flatMap(p => p.items)
  const habitues = `${th.regularsVisits} visites ou plus en ${duree(th.regularsDays)}`
  const revenus = `2 visites ou plus, vu·e depuis moins de ${th.lapsedDays} jours`
  /* v3.3 WF-025 · the file is addressable: /clients?vue=profil&client=<id> */
  const ouvrir = (r: ClientRow) => { setChoisi(r); setVue('profil'); setParams({ vue: 'profil', client: r.id }, { replace: true }) }
  const liste = () => { setVue('liste'); setParams({}, { replace: true }) }
  const campaignNames: Record<string, string> = Object.fromEntries([...allCampaigns(apiCampaigns, Object.fromEntries((locations ?? []).map(l => [l.id, l.name]))).map(c => [c.id, c.name] as const), ...getCampaigns().map(c => [c.id, c.name] as const)])
  const ledger = getLedger()

  const retour = <p className="f-help" style={{ margin: 0 }}>
    <a href="#" onClick={e => { e.preventDefault(); liste() }}>‹ Clients</a></p>

  /* ---------- v3.3 WF-011 · « Cadeaux gagnés » lives under Clients (crumb « Tous les clients »); /resultats?vue=cadeaux keeps the scoped view ---------- */
  if (vue === 'cadeaux') return (
    <div className="page" data-testid="clients-cadeaux">
      <PageHeader crumb={<a href="#" onClick={e => { e.preventDefault(); liste() }}>Tous les clients</a>} title="Cadeaux gagnés" description="Retrouver un cadeau existant, son état et son historique."
        actions={<Button size="sm" variant="quiet" onClick={liste}>Tous les clients</Button>} />
      <GiftLedger rows={ledger} campaigns={Object.entries(campaignNames).map(([id, name]) => ({ id, name }))} />
    </div>
  )

  /* ---------- CR-02 · customer profile — permitted fields only, the API's row; v3.3 WF-025 · her gifts, her BMR activity,
     the salon's communications, the BMR history and the recovery of an EXISTING gift (never a new win) ---------- */
  const wanted = params.get('client')
  const fiche = choisi ?? (wanted ? items.find(r => r.id === wanted) : undefined) ?? items[0]
  if (vue === 'profil' && fiche) {
    const mine = rowsOfClient(ledger, fiche.id).slice().sort((x, y) => (x.at < y.at ? 1 : -1))
    const gifts = mine.filter(r => r.gift)
    /* the history: one line per fact, newest first */
    const history: { at: string; what: string; ctx: string; code?: string }[] = []
    for (const r of mine) {
      const ctx = campaignNames[r.campaignId] ?? r.campaignId
      history.push({ at: r.at, what: 'Participation', ctx })
      if (r.invitation) { history.push({ at: r.at, what: 'Demande proposée', ctx }); if (r.invitation.opened) history.push({ at: r.at, what: 'Invitation ouverte', ctx }) }
      if (r.ask) history.push({ at: r.at, what: 'Réponse à une demande', ctx })
      if (r.gift) { history.push({ at: r.at, what: 'Cadeau attribué', ctx, code: r.gift.code }); if (r.gift.usedAt) history.push({ at: r.gift.usedAt, what: 'Cadeau utilisé', ctx, code: r.gift.code }) }
    }
    history.sort((x, y) => (x.at < y.at ? 1 : -1))
    const recoverable = gifts.filter(r => r.gift!.status === 'disponible')
    return (
      <div style={{ display: 'grid', gap: 16, maxWidth: 920, gridTemplateColumns: 'minmax(0, 1fr)' /* v2 · the history table scrolls inside, never the page (390 px, M3) */ }} data-testid="client-fiche">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{retour}<span style={{ marginLeft: 'auto' }}><Button size="sm" variant="quiet" onClick={liste}>Tous les clients</Button></span></div>
        <div>
          <h2 className="t-page" style={{ margin: 0 }}>{nom(fiche)}</h2>
          <p className="f-help" style={{ margin: '4px 0 0' }}>Activité connue dans les parcours BMR.</p>
        </div>
        <Card>
          <KV rows={[
            ['Contact', fiche.email ? <span key="c">{fiche.email}{fiche.emailConfirmed === false && <> · <span data-testid="adresse-non-confirmee" style={{ color: 'var(--bmr-text-muted)' }}>adresse non confirmée</span></>}</span> : 'visible par le propriétaire et les administrateurs'],
            ['Visites', `${visites(fiche.visits)} · dernière le ${jour(fiche.lastVisitAt)}`],
            ['Étape', <Pill key="e" tone={STAGE[fiche.stage].tone}>{STAGE[fiche.stage].label}</Pill>],
            ['Cadeaux', cadeaux(fiche)],
          ]} />
        </Card>
        {fiche.email && fiche.emailConfirmed === false && <Note tone="candour"><b>Adresse non confirmée.</b> Le client n’a pas encore ouvert le lien de son e-mail. Son cadeau l’attend quand même&nbsp;; l’adresse sera confirmée à son premier clic.</Note>}
        {fiche.stage === 'perdu_de_vue'
          ? <Note tone="candour">Pas de visite depuis plus de {th.lapsedDays}&nbsp;jours&nbsp;: un client à réinviter.</Note>
          : fiche.giftsHeld > 0 ? <Note tone="ok">Un cadeau l’attend&nbsp;: une bonne raison de revenir.</Note> : null}
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
            <Card data-testid="ses-cadeaux">
              <h3 className="t-section" style={{ margin: '0 0 8px', fontSize: 17 }}>Ses cadeaux</h3>
              {gifts.length === 0 ? <p className="f-help" style={{ margin: 0 }}>{ledger.length ? 'Aucun cadeau connu dans BMR pour ce client.' : `Ses cadeaux : ${NOT_MEASURED}.`}</p>
                : gifts.map(r => <ListRow key={r.id} title={r.gift!.typeName} meta={`${r.gift!.code} · ${dateFr(r.gift!.expiresAt)}`}
                  end={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><Pill tone={STATUS[r.gift!.status].tone}>{STATUS[r.gift!.status].label}</Pill><Button size="sm" variant="quiet" onClick={() => nav(`${giftDetailPath(r.gift!.code)}&retour=clients`)}>Ouvrir</Button></span>} />)}
            </Card>
            <Card data-testid="son-activite">
              <h3 className="t-section" style={{ margin: '0 0 8px', fontSize: 17 }}>Son activité BMR</h3>
              <p className="t-body" style={{ margin: 0 }}>{visites(fiche.visits)} connue{fiche.visits > 1 ? 's' : ''} · dernière le {dateFr(fiche.lastVisitAt)}.</p>
              <p className="f-help" style={{ margin: '4px 0 0' }}>Ce nombre n’est pas le nombre total de rendez-vous au salon.</p>
            </Card>
          </div>
          <Card data-testid="communications">
            <h3 className="t-section" style={{ margin: '0 0 8px', fontSize: 17 }}>Communications du salon</h3>
            <p style={{ margin: 0 }}><Pill tone="ink">Accord marketing · {NOT_MEASURED}</Pill></p>
            {fiche.email && <p className="t-body" style={{ margin: '8px 0 0' }}>{fiche.email}</p>}
            <p className="f-help" style={{ margin: '6px 0 10px' }}>L’accord marketing n’est pas encore enregistré dans BMR&nbsp;: une adresse utilisée pour recevoir un cadeau n’est pas un accord. Aucun message ne part d’ici.</p>
            <Button size="sm" disabled title="Nécessite un accord enregistré et l’expéditeur configuré dans Messages.">Écrire à ce client</Button>
          </Card>
        </div>
        <Card data-testid="historique-bmr">
          <h3 className="t-section" style={{ margin: '0 0 8px', fontSize: 17 }}>Historique BMR</h3>
          {history.length === 0 ? <p className="f-help" style={{ margin: 0 }}>{ledger.length ? 'Aucune participation connue dans BMR pour ce client.' : `Historique BMR : ${NOT_MEASURED}.`}</p> : (
            <div className="tablewrap"><table className="tbl">
              <thead><tr><th>Date</th><th>Activité</th><th>Contexte</th></tr></thead>
              <tbody>{history.map((h, i) => <tr key={i}><td style={{ whiteSpace: 'nowrap' }}>{dateTimeFr(h.at)}</td><td>{h.what}</td><td>{h.ctx}{h.code && <> · <a href="#" onClick={e => { e.preventDefault(); nav(`${giftDetailPath(h.code!)}&retour=clients`) }}>Voir le cadeau existant</a></>}</td></tr>)}</tbody>
            </table></div>
          )}
        </Card>
        <Card data-testid="accord-marketing">
          <h3 className="t-section" style={{ margin: '0 0 8px', fontSize: 17 }}>Accord marketing</h3>
          <p className="t-body" style={{ margin: 0 }}>Aucun accord enregistré dans cette version — {NOT_MEASURED}.</p>
          <p className="f-help" style={{ margin: '6px 0 10px' }}>Un lien de récupération sécurisé nécessite le service connecté. Vous pouvez retrouver le cadeau existant&nbsp;; aucun nouveau gain n’est créé.</p>
          {recoverable.length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{recoverable.map(r => <Button key={r.id} size="sm" onClick={() => nav(`${giftDetailPath(r.gift!.code)}&retour=clients`)}>Retrouver {r.gift!.typeName}</Button>)}</div>}
        </Card>
        <p className="f-help">Fiche limitée à votre Workspace — jamais le portefeuille d’un client chez d’autres marchands.</p>
      </div>
    )
  }

  /* ---------- CR-03 · segment builder — consent-aware by construction ---------- */
  if (vue === 'segment') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Segment — «&nbsp;Reviennent bientôt&nbsp;»</h2>
      <Card>
        <KV rows={[
          ['Étape', <span key="1">est 2 ou 3 · <a href="#" onClick={e => e.preventDefault()}>modifier</a></span>],
          ['Prochaine éligibilité', <span key="2">dans les 14 jours · <a href="#" onClick={e => e.preventDefault()}>modifier</a></span>],
          ['Cadeau actif', <span key="3">= oui · <a href="#" onClick={e => e.preventDefault()}>modifier</a></span>],
        ]} />
      </Card>
      <Note tone="ok">142 clients inclus · <b>18 exclus selon leurs préférences</b> — l’aperçu montre toujours les deux chiffres.</Note>
      <p className="f-help">Dimensions permises&nbsp;: établissement, campagne, étape, éligibilité, état cadeau, utilisation, consentement, dernière activité, résultat quiz divulgué. Jamais&nbsp;: comportement inter-marchands, sentiment privé pour router un avis public.</p>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="primary" onClick={() => setVue('activation')}>Activer ce segment</Button>
        <Button variant="quiet">Envoyer un test</Button>
      </div>
      <p className="f-help">Consentement & suppressions revérifiés <b>à chaque exécution</b> — pas seulement à la création. Exclusions listées avec motif.</p>
    </div>
  )

  /* ---------- CR-04 · suppression & data rights ---------- */
  if (vue === 'droits') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Suppression & droits des données</h2>
      <section>
        <ListRow title="Demande d’effacement — Marc D." meta="reçue le 27 août · échéance 26 sept."
          end={<Pill tone="warn">En cours</Pill>} />
        <ListRow title="Désinscription marketing — 6 clients" meta="appliquée immédiatement, propagée aux connecteurs"
          end={<Pill tone="done">Fait</Pill>} />
      </section>
      <p className="f-help">Effacement&nbsp;: le profil et les préférences disparaissent&nbsp;; les preuves promotion/facturation/fraude sont pseudonymisées et conservées sur base légale — le client en est informé.</p>
    </div>
  )

  /* ---------- CR-05 · data activation builder — can never touch gifts or bypass consent ---------- */
  if (vue === 'activation') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Activer ce segment</h2>
      <Card>
        <KV rows={[
          ['Source', 'Segment « Reviennent bientôt » (142)'],
          ['Destination', <span key="d"><b>Google Sheets — feuille Relances</b> · Zapier, webhook signé possibles</span>],
          ['Mode', 'hebdomadaire, lundi 08:00 · unique / temps réel possibles'],
          ['Champs', <span key="f">prénom, établissement, étape, date d’éligibilité — <a href="#" onClick={e => e.preventDefault()}>aperçu</a></span>],
        ]} />
      </Card>
      <Note tone="ok">Consentement &amp; suppressions revérifiés <b>à chaque exécution</b> — pas seulement à la création. Exclusions listées avec motif.</Note>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="quiet">Envoyer un test</Button>
        <Button variant="primary" onClick={() => setVue('historique')}>Activer</Button>
      </div>
      <p className="f-help">Une activation ne peut jamais émettre ou annuler un cadeau, ni contourner un consentement.</p>
    </div>
  )

  /* ---------- CR-11 · integrations hub — every card an honest state ---------- */
  if (vue === 'integrations') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Intégrations</h2>
      <section>
        <ListRow title="Export CSV" meta="toutes offres · gouverné, définitions incluses" end={<Pill tone="ok">Disponible</Pill>} />
        <ListRow title="Google Sheets" meta="natif · ajout/synchro de champs approuvés"
          end={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Pill tone="ok">Connecté</Pill><Button size="sm" variant="quiet" onClick={() => setVue('connexion')}>Détail</Button></span>} />
        <ListRow title="Zapier / Make" meta="scénarios documentés — pas encore d’app officielle" end={<Pill tone="done">Scénario</Pill>} />
        <ListRow title="Google Tag Manager" meta="deux usages distincts — voir le guide"
          end={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Pill tone="warn">À configurer</Pill><Button size="sm" variant="quiet" onClick={() => setVue('gtm')}>Configurer</Button></span>} />
        <ListRow title="GA4 direct · Webhooks signés" meta="selon offre · état honnête" end={<Pill tone="done">Planifié</Pill>} />
        <ListRow title="Passes Google / Apple Wallet" meta={<span>Google&nbsp;: démo vérifiée · Apple&nbsp;: compte approuvé, certificat à émettre</span>} end={<Pill tone="warn">Statut</Pill>} />
        <ListRow title="API publique & clés" meta="plus tard (Stage 7) — backlog préservé" end={<Pill tone="done">Plus tard</Pill>} />
      </section>
      <p className="f-help">Chaque carte&nbsp;: rôle, règle de disponibilité, sens des données, établissements concernés, méthode d’installation, dernière vérification.</p>
    </div>
  )

  /* ---------- CR-12 · connection detail — downgrade pauses, never deletes (locked #21) ---------- */
  if (vue === 'connexion') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }}>
      <p className="f-help" style={{ margin: 0 }}>
        <a href="#" onClick={e => { e.preventDefault(); setVue('integrations') }}>‹ Intégrations</a></p>
      <h2 className="t-page" style={{ margin: 0 }}>Google Sheets — Relances</h2>
      <Card>
        <KV rows={[
          ['État', <span key="e"><Pill tone="ok">Connecté</Pill> · dernière activité il y a 4 min</span>],
          ['Établissements concernés', 'tous · chaque établissement hérite du réglage'],
          ['Objets autorisés', 'segments, événements approuvés — champs listés'],
          ['Feuille / clé de ligne', '« Relances » · e-mail (dédupliqué)'],
        ]} />
      </Card>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="quiet">Envoyer un test</Button>
        <Button variant="quiet">Mettre en pause</Button>
        <Button variant="danger">Déconnecter</Button>
        <Button variant="quiet" onClick={() => setVue('historique')}>Historique</Button>
      </div>
      <Note tone="candour">Identifiants chiffrés, côté serveur, rotatifs — jamais dans le navigateur ni les journaux. Déconnexion&nbsp;: la config et l’audit restent, les envois s’arrêtent. Une panne du connecteur ne touche jamais le parcours client ni les cadeaux.</Note>
    </div>
  )

  /* ---------- CR-13 · GTM — the two products named separately (§54.2) ---------- */
  if (vue === 'gtm') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <p className="f-help" style={{ margin: 0 }}>
        <a href="#" onClick={e => { e.preventDefault(); setVue('integrations') }}>‹ Intégrations</a></p>
      <h2 className="t-page" style={{ margin: 0 }}>Google Tag Manager — deux usages distincts</h2>
      <section>
        <ListRow title="1 · Installer un support BMR sur votre site" meta="votre site, votre consentement, votre conteneur — modèle fourni"
          end={<Button size="sm" variant="quiet">Guide</Button>} />
        <ListRow title="2 · Envoyer les événements de parcours vers votre GTM" meta="ID conteneur Workspace · catégorie de consentement · aperçu des événements"
          end={<Button size="sm" variant="quiet">Configurer</Button>} />
      </section>
      <Note tone="candour">Vos tags ne se chargent jamais sur&nbsp;: connexion, Mes cadeaux, jetons de validation, vérificateur, administration. Ils ne partent qu’après le consentement applicable — le refus ne casse jamais le parcours ni le cadeau. Validation du conteneur + test + santé incluses.</Note>
      <p className="f-help">Les charges analytiques ne portent jamais de données personnelles ni de codes cadeaux (§54.3).</p>
    </div>
  )

  /* ---------- CR-14 · delivery log & health — one honest view ---------- */
  if (vue === 'journal') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Journal des envois &amp; santé</h2>
      <div className="tablewrap">
        <table className="tbl">
          <thead><tr><th>Événement</th><th>Connecteur</th><th>État</th></tr></thead>
          <tbody>
            <tr><td>bmr_gift_issued</td><td>Sheets</td><td><Pill tone="ok">Accusé</Pill></td></tr>
            <tr><td>bmr_participation_committed</td><td>Webhook</td><td><Pill tone="warn">Relance 2/5</Pill></td></tr>
            <tr><td>bmr_gift_redeemed</td><td>Sheets</td><td><span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><Pill tone="crit">Lettre morte</Pill><a href="#" onClick={e => e.preventDefault()}>relancer</a></span></td></tr>
          </tbody>
        </table>
      </div>
      <Tiles>
        <Tile label="Envoyés 24 h" value="1 204" hint="99,2 % accusés" />
        <Tile label="Fraîcheur synchro" value="4 min" hint="Sheets · seuil 15" />
        <Tile label="Services" value="OK" hint="e-mail · Google · kiosques" />
      </Tiles>
      <p className="f-help">Charges utiles masquées&nbsp;; relance manuelle tracée, idempotente. Un accusé prouve la réception — jamais qu’une automatisation a tourné ni qu’un avis a été écrit.</p>
    </div>
  )

  /* ---------- CR-15 · segment library — saved segments have a home ---------- */
  if (vue === 'segments') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      {retour}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 className="t-page" style={{ margin: 0 }}>Segments enregistrés</h2>
        <span style={{ marginLeft: 'auto' }}><Button size="sm" variant="primary">Nouveau segment</Button></span>
      </div>
      <section>
        <ListRow title="Reviennent bientôt" meta="142 clients · utilisé par 1 activation · maj. il y a 2 h"
          end={<span><a href="#" onClick={e => { e.preventDefault(); setVue('segment') }}>modifier</a> · <a href="#" onClick={e => e.preventDefault()}>dupliquer</a></span>} />
        <ListRow title="VIP — étape 5" meta="38 clients · aucune activation"
          end={<a href="#" onClick={e => { e.preventDefault(); setVue('segment') }}>modifier</a>} />
        <ListRow title="Cadeaux expirant — 14 j" meta="61 clients · rappel automatique"
          end={<a href="#" onClick={e => { e.preventDefault(); setVue('segment') }}>modifier</a>} />
        <div style={{ opacity: .6 }}>
          <ListRow title="Test juillet" meta="archivé le 3 août" end={<a href="#" onClick={e => e.preventDefault()}>restaurer</a>} />
        </div>
      </section>
      <Note tone="candour">Un segment utilisé par une activation ne se supprime pas silencieusement&nbsp;: le produit indique où il sert, puis propose l’archivage.</Note>
    </div>
  )

  /* ---------- CR-16 · activation run history — consent rechecked at execution ---------- */
  if (vue === 'historique') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <p className="f-help" style={{ margin: 0 }}>
        <a href="#" onClick={e => { e.preventDefault(); setVue('activation') }}>‹ Activation</a></p>
      <h2 className="t-page" style={{ margin: 0 }}>Historique — Sheets «&nbsp;Relances&nbsp;»</h2>
      <div className="tablewrap">
        <table className="tbl">
          <thead><tr><th>Exécution</th><th>Inclus</th><th>Exclus</th><th>Résultat</th></tr></thead>
          <tbody>
            <tr><td>Lun. 25 août, 08:00</td><td>142</td><td>18</td><td><Pill tone="ok">Livré</Pill></td></tr>
            <tr><td>Lun. 18 août, 08:00</td><td>131</td><td>15</td><td><Pill tone="ok">Livré</Pill></td></tr>
            <tr><td>Lun. 11 août, 08:00</td><td>—</td><td>—</td><td><Pill tone="crit">Connexion expirée</Pill></td></tr>
          </tbody>
        </table>
      </div>
      <ListRow title="Exclusions du 25 août" meta="12 sans consentement marketing · 4 supprimés · 2 e-mails invalides"
        end={<a href="#" onClick={e => e.preventDefault()}>voir le détail</a>} />
      <p className="f-help">Un accusé prouve que le destinataire a accepté la charge — jamais qu’une automatisation a tourné en aval.</p>
    </div>
  )

  /* ---------- CR-17 · day-one empty — what a group is FOR ---------- */
  if (d.known === 0) return (
    <EmptyState title="Vos clients apparaîtront ici dès la première participation.">
      <p className="t-body" style={{ maxWidth: 580 }}>Chaque personne qui scanne votre support et joue est enregistrée ici&nbsp;: son prénom, ses visites, les cadeaux qu’elle a gagnés et utilisés. C’est ce qui vous permet de voir <b>qui revient</b> — la seule chose que cette page existe pour répondre.</p>
      <p className="t-body" style={{ maxWidth: 580 }}><b>À quoi servent les groupes&nbsp;?</b> Un groupe rassemble des clients qui se ressemblent — «&nbsp;revenus au moins deux fois&nbsp;», «&nbsp;n’ont jamais utilisé leur cadeau&nbsp;», «&nbsp;pas revenus depuis {th.lapsedDays}&nbsp;jours&nbsp;». Vous pourrez leur envoyer un message adapté au lieu du même message pour tout le monde. Rien à préparer maintenant&nbsp;: les groupes se créeront tout seuls quand il y aura des clients dedans.</p>
      <p className="f-help" style={{ maxWidth: 580 }}>C’est vide parce que personne n’a encore scanné votre support. Comptez quelques heures après l’avoir posé sur le comptoir.</p>
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <Button variant="primary" onClick={() => nav('/supports')}>Imprimer mon support</Button>
        <Button onClick={() => window.open('/d/demo', '_blank')}>Tester le parcours sur mon téléphone</Button>
      </div>
    </EmptyState>
  )

  /* ---------- CR-01 · cohorts lead, the directory is a tab ---------- */
  const shown = (tab === 'reviennent'
    ? items.filter(r => r.visits > 1 && r.stage !== 'perdu_de_vue')
    : items.filter(FILTRES[filtre])).filter(r => matches(r, q))
  const voir = (f: Filtre) => { setFiltre(f); setTab('tous') }
  return (
    <div className="page">
      <PageHeader title="Clients" description={<>Des groupes fondés sur l’activité observée dans BMR. Qui réinviter, et quand&nbsp;: ceux qui reviennent, ceux qu’un cadeau attend, ceux qui s’éloignent. Chaque définition est sur sa carte.{ctxName ? <> <b>{ctxName}</b>&nbsp;: les mêmes fiches, filtrées sur ceux qui ont joué ou utilisé un cadeau ici — un client reste un seul client dans tout le réseau.</> : ''}</>}
        actions={<>
          <Button size="sm" variant="quiet" onClick={() => { setVue('cadeaux'); setParams({ vue: 'cadeaux' }, { replace: true }) }}>Cadeaux gagnés</Button>
          <Button size="sm" variant="quiet" onClick={() => setVue('segments')}>Segments</Button>
          <Button size="sm" variant="quiet" onClick={() => setVue('integrations')}>Intégrations</Button>
          <Button size="sm" variant="quiet" onClick={() => setVue('journal')}>Journal des envois</Button>
          <Button size="sm" variant="quiet" onClick={() => setVue('droits')}>Droits des données</Button>
          {/* Exporter promoted — owner call, 02/09 */}
          <Button size="sm" variant="primary" onClick={() => nav('/resultats?vue=export')}><Icon name="external" /> Exporter</Button>
        </>}
        tabs={<Tabs value={tab} onChange={v => setTab(v as typeof tab)} options={[['reviennent', 'Qui revient'], ['tous', 'Tous les clients'], ['groupes', 'Groupes']]} />} />
      <div data-testid="clients-tuiles"><Tiles>
        <Tile hero label={<>Revenus ce mois<Hint label="Définition">Une visite ce mois-ci et au moins une avant — une première visite ne compte pas encore.</Hint></>}
          value={d.returningThisMonth} hint={`sur ${d.known.toLocaleString('fr-FR')} clients connus`} />
        <Tile label={<>Habitués<Hint label="Définition">{habitues} — ceux qui font vivre l’établissement.</Hint></>}
          value={d.regulars} hint={habitues} />
        <Tile label={<>Cadeau non utilisé<Hint label="Définition">Un cadeau actif les attend — un rappel les fait revenir&nbsp;: le groupe est prêt à relancer.</Hint></>}
          value={d.unusedGift} hint="à relancer" />
        <Tile label={<>Perdus de vue<Hint label="Définition">Aucune visite depuis plus de {th.lapsedDays}&nbsp;jours — le groupe à réinviter.</Hint></>}
          value={d.lapsed} hint={`> ${th.lapsedDays} jours`} />
      </Tiles></div>

      {tab === 'groupes' ? (
        <section data-testid="clients-groupes">
          <ListRow title="« Habitués »" meta={`${d.regulars.toLocaleString('fr-FR')} clients · ${habitues}`}
            end={<Button size="sm" variant="quiet" onClick={() => voir('habitue')}>Voir</Button>} />
          <ListRow title="« Revenus »" meta={`${d.returningThisMonth.toLocaleString('fr-FR')} clients ce mois · ${revenus}`}
            end={<Button size="sm" variant="quiet" onClick={() => voir('revenu')}>Voir</Button>} />
          <ListRow title="« Cadeau non utilisé »" meta={`${d.unusedGift.toLocaleString('fr-FR')} clients · un cadeau actif les attend`}
            end={<Button size="sm" variant="quiet" onClick={() => voir('cadeau')}>Voir</Button>} />
          <ListRow title="« Perdus de vue »" meta={`${d.lapsed.toLocaleString('fr-FR')} clients · aucune visite depuis plus de ${th.lapsedDays} jours`}
            end={<Button size="sm" variant="quiet" onClick={() => voir('perdu')}>Voir</Button>} />
          <p className="f-help" style={{ margin: '10px 0 0' }}>
            Ces groupes disent qui réinviter. L’envoi d’un message à tout un groupe arrive bientôt&nbsp;; d’ici là,{' '}
            <a href="#" onClick={e => { e.preventDefault(); nav('/messages/revenez-jouer') }}>le message «&nbsp;Revenez jouer&nbsp;»</a> réinvite chaque client le jour où il peut rejouer.
          </p>
        </section>
      ) : (
        <>
          {/* v3.3 WF-024 · search, the groups (« Revenus » added), the communications filter — honest: no consent field yet */}
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'end' }} data-testid="clients-recherche">
            <label className="f-label" style={{ margin: 0 }}>Rechercher un client<Input aria-label="Rechercher un client" value={q} onChange={e => setQ(e.target.value)} placeholder="Nom, mot-clé…" style={{ marginTop: 6 }} /></label>
            <label className="f-label" style={{ margin: 0 }}>Communications<Select aria-label="Communications" value="tous" disabled title={`Accord marketing : ${NOT_MEASURED} dans cette version.`} style={{ marginTop: 6 }}>
              <option value="tous">Tous les accords</option><option value="accord">Accord enregistré</option><option value="sans">Sans accord</option>
            </Select></label>
          </div>
          {tab === 'tous' && (
            <div data-testid="clients-filtre"><span className="f-label">Groupe</span><Segmented value={filtre} onChange={v => setFiltre(v as Filtre)}
              options={[['tous', 'Tous les clients'], ['habitue', 'Habitués'], ['revenu', 'Revenus'], ['cadeau', 'Cadeau à utiliser'], ['perdu', 'Perdus de vue']]} />
              {filtre === 'revenu' && <p className="f-help" style={{ margin: '6px 0 0' }}>Revenus&nbsp;: {revenus}.</p>}</div>
          )}
          <p className="f-help" style={{ margin: 0 }} data-testid="clients-compte">{shown.length.toLocaleString('fr-FR')} résultat{shown.length > 1 ? 's' : ''}</p>
          <div data-testid="clients-liste"><Card>
            {shown.length === 0 ? (
              <p className="f-help" style={{ margin: 0 }}>{tab === 'reviennent'
                ? 'Personne n’est encore revenu dans cette liste. Les clients apparaissent ici dès leur deuxième visite.'
                : 'Aucun client dans ce groupe pour l’instant.'}</p>
            ) : (
              <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--bmr-text-muted)' }}>
                  <th style={{ padding: '6px 8px' }}>Client</th><th>Activité BMR</th><th>Étape</th><th>Cadeaux</th><th>Communications <Hint label="Communications">L’accord marketing n’est pas encore enregistré dans BMR&nbsp;: la colonne dit «&nbsp;{NOT_MEASURED}&nbsp;» plutôt qu’un état inventé.</Hint></th><th></th>
                </tr></thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--bmr-border)', cursor: 'pointer' }}
                      onClick={() => ouvrir(r)}>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600 }}>{nom(r)}</div>
                        {r.email && <div className="f-help" style={{ margin: 0 }}>{r.email}{r.emailConfirmed === false && <> · <span data-testid="adresse-non-confirmee" style={{ color: 'var(--bmr-text-muted)' }}>adresse non confirmée</span></>}</div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{visites(r.visits)}<div className="f-help" style={{ margin: 0 }}>Dernière&nbsp;: {jour(r.lastVisitAt)}</div></td>
                      <td><Pill tone={STAGE[r.stage].tone}>{STAGE[r.stage].label}</Pill></td>
                      <td>{cadeaux(r)}</td><td className="f-help">{NOT_MEASURED}</td>
                      <td><Button size="sm" variant="quiet" onClick={e => { e.stopPropagation(); ouvrir(r) }}>Voir la fiche</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Card></div>
          {hasNextPage && (
            <div><Button size="sm" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>Afficher les 50 suivants</Button></div>
          )}
          <p className="f-help">{d.known.toLocaleString('fr-FR')} clients · profil isolé à votre Workspace — jamais le portefeuille d’un client chez d’autres marchands. Les groupes peuvent se recouper. Une adresse utilisée pour recevoir un cadeau n’est pas un accord marketing. Filtrer une liste n’envoie aucun message.</p>
        </>
      )}
    </div>
  )
}
